const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface AuthUser {
  id: number;
  username: string;
  email?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type?: string;
  user?: AuthUser;
}

export interface RegisterResponse {
  access_token?: string;
  token_type?: string;
  user?: AuthUser;
  message?: string;
}

export interface DefinitionEntry {
  part_of_speech: string;
  definition: string;
}

export interface ExampleEntry {
  sentence: string;
  translation?: string;
  source?: string;
  author?: string;
  language?: string;
}

export interface LexiconResponse {
  word: string;
  language?: string;
  pronunciation?: string;
  translation?: string[];
  definition?: DefinitionEntry[];
  definitions?: DefinitionEntry[];
  examples?: ExampleEntry[];
}

export interface WordContextItem {
  scene: string;
  example: string;
  translation?: string;
}

export interface LexiconContextResponse {
  contexts?: WordContextItem[];
  items?: WordContextItem[];
}

export interface VocabGraphNode {
  id?: string;
  word: string;
  type: 'synonym' | 'antonym' | 'derivative' | 'collocate' | 'hypernym' | 'hyponym' | string;
}

export interface VocabGraphResponse {
  word: string;
  nodes: VocabGraphNode[];
}

export interface VocabRecommendation {
  id?: string | number;
  word: string;
  level?: string;
  definition?: string;
  translation?: string | string[];
  topic?: string;
  domain?: string;
  related_word?: string;
  reason?: string;
}

export interface VocabRadarResponse {
  domains?: Record<string, number>;
  level_distribution?: {
    advanced?: number;
    intermediate?: number;
    basic?: number;
  };
  total_words?: number;
  weakest_domain?: string;
  recommendations?: VocabRecommendation[];
}

export interface GrammarCheckResponse {
  corrected_text?: string;
  corrections?: string[];
  explanation?: string;
}

export interface TranslationResponse {
  translated_text?: string;
  translation?: string;
  detected_language?: string;
}

export interface ReadingHighlight {
  word: string;
  part_of_speech?: string;
  definition?: string;
  difficulty?: string;
}

export interface GrammarNote {
  title: string;
  sentence: string;
  explanation: string;
}

export interface QuizQuestion {
  question: string;
  options: string[];
  answer: string;
  explanation: string;
}

export interface ReadingAnalysisResponse {
  title?: string;
  category?: string;
  difficulty?: string;
  word_count?: number;
  original_text?: string;
  summary?: string;
  vocab_highlights?: ReadingHighlight[];
  grammar_notes?: GrammarNote[];
  quiz_questions?: QuizQuestion[];
}

export interface CreateChatSessionParams {
  target_language: string;
  level: string;
  scenario: string;
  focus_words?: string[];
}

export interface ChatSessionResponse {
  session_id: string;
  opening_message?: string;
  message?: string;
}

export function getToken(): string | null {
  return localStorage.getItem('access_token');
}

export function authHeaders(): HeadersInit {
  const token = getToken();
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>) {
  const url = new URL(path, BASE_URL);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const errorBody = await response.json();
      message = errorBody?.detail || errorBody?.message || errorBody?.error?.message || message;
    } catch {
      const text = await response.text();
      if (text) {
        message = text;
      }
    }
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  params?: Record<string, string | number | undefined>
): Promise<T> {
  const headers = { ...authHeaders(), ...(options.headers || {}) };
  const response = await fetch(buildUrl(path, params), {
    ...options,
    headers,
  });
  return parseResponse<T>(response);
}

export function login(username: string, password: string) {
  return request<LoginResponse>('/api/v1/user/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export function register(username: string, password: string, email?: string) {
  return request<RegisterResponse>('/api/v1/user/register', {
    method: 'POST',
    body: JSON.stringify({ username, password, email }),
  });
}

export function getMe() {
  return request<AuthUser>('/api/v1/user/me', { method: 'GET' });
}

export function lookupWord(text: string, lang: string) {
  return request<LexiconResponse>('/api/v1/lexicon/', { method: 'GET' }, { text, lang });
}

export function getWordContext(text: string, lang?: string) {
  return request<LexiconContextResponse | WordContextItem[]>('/api/v1/lexicon/context', { method: 'GET' }, { text, lang });
}

export function getVocabGraph(word: string, lang?: string, depth?: number) {
  return request<VocabGraphResponse>('/api/v1/vocab/graph', { method: 'GET' }, { word, lang, depth });
}

export function getVocabRadar() {
  return request<VocabRadarResponse>('/api/v1/vocab/radar', { method: 'GET' });
}

export function getRecommendations(domain?: string) {
  return request<VocabRecommendation[] | { items?: VocabRecommendation[]; recommendations?: VocabRecommendation[] }>(
    '/api/v1/vocab/recommendations',
    { method: 'GET' },
    { domain }
  );
}

export function checkGrammar(text: string, target_language?: string) {
  return request<GrammarCheckResponse>('/api/v1/grammar/', {
    method: 'POST',
    body: JSON.stringify({ text, target_language }),
  });
}

export function translate(text: string, target_language?: string) {
  return request<TranslationResponse>('/api/v1/translate/', {
    method: 'POST',
    body: JSON.stringify({ text, target_language }),
  });
}

export function analyzeReading(text: string, user_lang?: string) {
  return request<ReadingAnalysisResponse>('/api/v1/reading/analyze', {
    method: 'POST',
    body: JSON.stringify({ text, user_lang }),
  });
}

export function createChatSession(params: CreateChatSessionParams) {
  return request<ChatSessionResponse>('/api/v1/chat/session', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function getAudioUrl(word: string) {
  return `${BASE_URL}/api/v1/audio/${encodeURIComponent(word)}.mp3`;
}

export async function streamChatMessage(
  session_id: string,
  message: string,
  onDelta: (delta: string) => void,
  onDone: (fullMessage: string) => void
) {
  const response = await fetch(
    buildUrl('/api/v1/chat/message', {
      session_id,
      message,
    }),
    {
      method: 'GET',
      headers: {
        ...authHeaders(),
        Accept: 'text/event-stream',
      },
    }
  );

  if (!response.ok || !response.body) {
    await parseResponse(response);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  let fullMessage = '';
  let completed = false;

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffered += decoder.decode(value, { stream: true });
    const chunks = buffered.split(/\n\n/);
    buffered = chunks.pop() || '';

    for (const chunk of chunks) {
      const lines = chunk
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

      if (lines.some((line) => line === 'data: [DONE]')) {
        onDone(fullMessage);
        completed = true;
        return;
      }

      const dataLines = lines
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.replace(/^data:\s*/, ''));

      const delta = dataLines.length > 0 ? dataLines.join('\n') : chunk;
      if (delta) {
        fullMessage += delta;
        onDelta(delta);
      }
    }
  }

  if (!completed) {
    onDone(fullMessage);
  }
}
